import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { ProviderCard, type QuickBookGroup } from '@/components/cards';
import { PatientAvatar } from '@/shared/ui/components';
import { SkeletonBar } from '@/components/blocks/calendar-skeleton';
import type {
  AppointmentSlot,
  CreateAppointmentProfileRequest,
  ProviderIssue,
  SlotResponse,
} from '@/types/scheduling-api';

/**
 * ProviderStrip — Block
 * The providers (practitioners, or devices on the Devices tab) above the calendar.
 *
 * Compact (Antonin, 13 Oct, to give the grid its room back): one 40 px row of mini chips — photo, availability dot,
 * name, today's free slots, a dot for a licence issue, a blue ring when ticked in the filter. Hovering a chip reveals
 * the full card (ProviderCard layout="detail", every action of the old card: settings link, ✓, slot popover, Next
 * quick-create, quick-book chips); a click pins it open until Escape or a click outside. Chips that don't fit go
 * behind "+N", whose hover dropdown shows those providers' full cards in a grid. Was: a wrapping grid of 250 px cards
 * (Flutter `calendar_tab.dart:3992-4441`) with an expand chevron in CalendarSubToolbar, which now never shows.
 */

export type { QuickBookGroup };

export interface ProviderSummary {
  id: string;
  name: string;
  specialty: string;
  initials: string;
  /** Avatar URL — falls back to initials when missing. */
  photo?: string;
  /** Credential/license issue flag (PROD-980) — drives the error/warning
   *  badge + tooltip on the card. Absent when the provider has no issue. */
  issue?: ProviderIssue | null;
  available: boolean;
  slotsToday: number;
  schedule: string;
  /** Formatted display strings for the "Next" line (e.g. "9:35a"). */
  nextSlots: string[];
  /**
   * Raw slot objects for the same "Next" entries — used to make the
   * inline chips clickable (PROD-829 quick-create path B). Same
   * length and index alignment as `nextSlots`. May be empty when
   * the upstream data hasn't carried raw slots through.
   */
  nextSlotResponses?: AppointmentSlot[];
  /**
   * Quick-book chips. Each entry is one location-group of appointment
   * profiles (mirrors Flutter's `groupedAppointmentProfile`). The chip
   * shows the duration of the first profile in the group; clicking opens
   * a popover listing every profile in that group.
   */
  quickBookGroups: QuickBookGroup[];
  /**
   * The provider's preferred-or-first appointment profile, selected from the
   * RAW original-order `appointmentProfile` list (NOT the re-sorted
   * `quickBookGroups`) so it matches Flutter `calendar_tab.dart:541-550`
   * exactly. Color is already CSS-normalized (same as the grouped profiles).
   * Consumed by the By-Doctor empty-cell quick-create (PROD-983). Null when
   * the provider has no profiles.
   */
  preferredProfile?: CreateAppointmentProfileRequest | null;
  /**
   * Set only with `?x=calendar-practice-slots`: the VIEWED practice the slot
   * popover asks for. Absent = today's behaviour (popover uses the preferred
   * profile's practice).
   */
  slotPracticeId?: string;
  /**
   * With `slotPracticeId`: the profile THE ONE RULE picked at that practice
   * (`practice-slot-profile.ts`); null = no profile there, the card shows
   * "No appointment profile at this practice".
   */
  slotProfile?: CreateAppointmentProfileRequest | null;
  /**
   * With `slotPracticeId`: the calendarSlots feed is still loading ('loading') or failed ('error'). The card then
   * shows no count, no "Off today" and no "No appointment profile at this practice"; 'error' offers Retry.
   */
  slotsState?: 'loading' | 'error';
}

interface ProviderStripProps {
  providers: ProviderSummary[];
  /** The practitioners are still on their way: chip-sized grey pills hold the row (same 40 px), nothing jumps. */
  loading?: boolean;
  /** Drawn at the start of the row, before the chips: the Practitioners / Devices switch (it had a row of its own). */
  leading?: React.ReactNode;
  /** Provider ids the user has explicitly checked (✓ on each card). */
  selectedProviderIds?: string[];
  onToggleProvider?: (providerId: string) => void;
  onProviderClick?: (providerId: string) => void;
  /**
   * Fired when a provider's NAME is clicked — opens that provider's settings
   * page. Flutter parity: `calendar_tab.dart:4215` (name-only InkWell →
   * DoctorBaseWidget). Provided only when the user has `Manage_Doctors` view
   * permission; otherwise names render as plain text.
   */
  onProviderNameClick?: (providerId: string) => void;
  /** Fired when the user picks a profile from a quick-book chip popover. */
  onSelectProfile?: (
    providerId: string,
    group: QuickBookGroup,
    profile: CreateAppointmentProfileRequest,
  ) => void;
  /** The hover card's "Book" location buttons: provider + location, no profile (see ProviderCard). */
  onBookLocation?: (
    providerId: string,
    group: QuickBookGroup,
    profile: CreateAppointmentProfileRequest,
  ) => void;
  /**
   * Controlled expand/collapse — the chevron lives in CalendarSubToolbar.
   * When `false`, only the first row of cards is rendered.
   */
  /** Every practitioner, on as many lines as it takes, no "+N" (the filter panel behind the Practitioners button). */
  wrap?: boolean;
  isExpanded?: boolean;
  /**
   * Fired whenever the strip detects whether overflow exists (more
   * providers than fit in a single row). The toolbar uses this to decide
   * whether to render its chevron.
   */
  onCanExpandChange?: (canExpand: boolean) => void;
  /**
   * Fired when a user picks a slot from the slot-count popover on any
   * card (PROD-828, Flutter `calendar_tab.dart:4320-4358`). Org/practice
   * IDs are read from `useAuthStore` inside `ProviderCard` — matches the
   * appointment-panel section pattern (Samer review §4 on PR #213).
   */
  onSelectSlot?: (
    providerId: string,
    slot: SlotResponse,
    profile: CreateAppointmentProfileRequest,
  ) => void;
  /** The cards' Retry when the calendarSlots feed failed (`slotsState: 'error'`): asks the feed again. */
  onRetrySlots?: () => void;
  /** The Retry request is running: the failed cards show "— Loading…" and disable Retry. */
  slotsRetrying?: boolean;
}

export function ProviderStrip({
  providers,
  leading,
  selectedProviderIds,
  onToggleProvider,
  onProviderClick,
  onProviderNameClick,
  onSelectProfile,
  onBookLocation,
  onCanExpandChange,
  onSelectSlot, onRetrySlots, slotsRetrying = false, wrap, loading = false}: ProviderStripProps) {
  const selectedSet = selectedProviderIds ? new Set(selectedProviderIds) : null;

  // Compact strip (Antonin, 13 Oct: "gain room in the calendar"): one 40 px row of mini chips; the full card shows on
  // hover. The old expand chevron has nothing left to expand.
  useEffect(() => {
    onCanExpandChange?.(false);
  }, [onCanExpandChange]);

  // How many chips fit on the row: an invisible copy of every chip is measured, and the rest go behind "+N".
  const rowRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState(providers.length);
  useLayoutEffect(() => {
    const row = rowRef.current;
    const measure = measureRef.current;
    if (!row || !measure) return;
    const recompute = () => {
      const widths = Array.from(measure.children).map((c) => (c as HTMLElement).offsetWidth);
      const room = row.clientWidth;
      let used = 0;
      let n = 0;
      for (const w of widths) {
        const next = used + (n > 0 ? CHIP_GAP : 0) + w;
        // Leave room for the "+N" chip unless every chip fits.
        const reserve = n + 1 < widths.length ? CHIP_GAP + MORE_WIDTH : 0;
        if (next + reserve > room) break;
        used = next;
        n++;
      }
      setFit(Math.max(1, n));
    };
    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(row);
    return () => ro.disconnect();
  }, [providers]);

  const [open, setOpen] = useState<{ id: string; pinned: boolean } | null>(null);
  const [expanded, setExpanded] = useState(false);
  // A chip near the right edge opens its card leftwards, so the card never leaves the frame.
  const [flip, setFlip] = useState(false);
  const placeFor = (el: HTMLElement) => {
    const strip = stripRef.current?.getBoundingClientRect();
    if (strip) setFlip(el.getBoundingClientRect().left + CARD_SPAN > strip.right);
  };
  const closeTimer = useRef<number | null>(null);
  const cancelClose = () => {
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };
  const showTimer = useRef<number | null>(null);
  const show = (id: string) => {
    cancelClose();
    if (showTimer.current !== null) window.clearTimeout(showTimer.current);
    // First open: 300ms delay to avoid accidental hovers. Once a card is
    // already showing, switch instantly (the pointer is intentionally browsing).
    setOpen((o) => {
      if (o) return o.pinned && o.id !== id ? o : { id, pinned: o.id === id ? o.pinned : false };
      // Nothing open — delay.
      showTimer.current = window.setTimeout(() => {
        setOpen({ id, pinned: false });
        showTimer.current = null;
      }, 300);
      return null;
    });
  };
  // A short grace period so the pointer can travel from the chip into its card (and into the card's own popovers).
  const hideSoon = () => {
    cancelClose();
    if (showTimer.current !== null) { window.clearTimeout(showTimer.current); showTimer.current = null; }
    closeTimer.current = window.setTimeout(() => setOpen((o) => (o?.pinned ? o : null)), 250);
  };
  useEffect(() => cancelClose, []);
  // A pinned card (clicked chip) closes on Escape or a click outside the strip.
  const stripRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open?.pinned) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      // Clicks inside the card's own popovers (portalled to <body>) don't close it.
      if (stripRef.current?.contains(t) || t.closest('[data-radix-popper-content-wrapper],[role="dialog"],[role="listbox"],[role="menu"],[data-slot="popover-content"]')) return;
      setOpen(null);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(null); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open?.pinned]);

  const card = (p: ProviderSummary) => (
    <ProviderCard
      layout="detail"
      name={p.name}
      specialty={p.specialty}
      initials={p.initials}
      photo={p.photo}
      issue={p.issue}
      available={p.available}
      slotsToday={p.slotsToday}
      schedule={p.schedule}
      nextSlots={p.nextSlots}
      nextSlotResponses={p.nextSlotResponses}
      quickBookGroups={p.quickBookGroups}
      isSelected={selectedSet ? selectedSet.has(p.id) : false}
      onToggleSelect={onToggleProvider ? () => onToggleProvider(p.id) : undefined}
      onClick={onProviderClick ? () => onProviderClick(p.id) : undefined}
      onNameClick={onProviderNameClick ? () => onProviderNameClick(p.id) : undefined}
      onSelectProfile={(g, profile) => onSelectProfile?.(p.id, g, profile)}
      onBookLocation={onBookLocation ? (g, profile) => onBookLocation(p.id, g, profile) : undefined}
      practitionerId={p.id}
      slotPracticeId={p.slotPracticeId}
      slotProfile={p.slotProfile}
      slotsState={p.slotsState}
      onRetrySlots={onRetrySlots}
      slotsRetrying={slotsRetrying}
      onSelectSlot={onSelectSlot ? (slot, profile) => onSelectSlot(p.id, slot, profile) : undefined}
    />
  );

  const narrowed = !!selectedSet && selectedSet.size > 0 && providers.some((p) => !selectedSet.has(p.id));
  // "+N" expands the strip in place onto as many lines as it needs (Antonin, 13 Oct: "expand the whole section in
  // several lines instead of hover for all cards"); "Less" folds it back to one line. Each chip keeps its badge on hover.
  const wrapping = wrap || expanded;
  // The glide: follow the content's height; clip only while the height is moving.
  const contentRef = useRef<HTMLDivElement>(null);
  const [boxHeight, setBoxHeight] = useState<number | null>(null);
  const [gliding, setGliding] = useState(false);
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => setBoxHeight(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const toggleExpanded = (next: boolean) => {
    setOpen(null);
    if (!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setGliding(true);
      // If the height doesn't change (no transition fires), stop clipping anyway.
      window.setTimeout(() => setGliding(false), 240);
    }
    setExpanded(next);
  };
  const shown = wrapping ? providers : providers.slice(0, fit);
  const rest = wrapping ? [] : providers.slice(fit);

  return (
    <div ref={stripRef} data-provider-strip className="relative border-b border-border-default bg-surface-card px-4">
      {/* Measuring copy: every chip, invisible, never interactive. */}
      <div ref={measureRef} aria-hidden inert className="pointer-events-none invisible absolute left-0 top-0 flex gap-2 whitespace-nowrap">
        {providers.map((p) => <MiniChip key={p.id} p={p} selected={false} />)}
      </div>
      {/* Unfolding and folding glide (Antonin, 13 Oct: "fast animation"): the box eases to its content's height in
          160 ms and clips only while it moves, so a chip's badge can still hang out of it at rest. */}
      <div
        className="transition-[height] duration-[160ms] ease-out motion-reduce:transition-none"
        style={{ height: boxHeight ?? undefined, overflow: gliding ? 'hidden' : undefined }}
        onTransitionEnd={(e) => { if (e.target === e.currentTarget) setGliding(false); }}
      >
      <div ref={contentRef} className={cn('flex items-center gap-3', wrapping ? 'py-2' : 'py-2')}>
      {leading ? <div className="flex flex-shrink-0 items-center border-r border-border-default pr-3">{leading}</div> : null}
      <div ref={rowRef} className={cn('flex min-w-0 flex-1 items-center gap-2', wrapping && 'flex-wrap')} role="list" aria-label="Providers">
        {loading && providers.length === 0 && SKELETON_CHIPS.map((w, i) => (
          <span key={i} aria-hidden data-skeleton-chip className="flex shrink-0 items-center gap-3 rounded-lg border border-border-subtle py-1 pl-2 pr-3" style={{ width: w }}>
            <SkeletonBar className="h-8 w-8 shrink-0 rounded-lg" />
            <SkeletonBar className="h-3 flex-1" />
          </span>
        ))}
        {shown.map((p) => (
          <div key={p.id} role="listitem" className="relative" onMouseEnter={(e) => { placeFor(e.currentTarget); show(p.id); }} onMouseLeave={hideSoon}>
            <MiniChip
              p={p}
              // Ticked shows only when it narrows: all ticked (the default) is no information (rule: a colour shared
              // by every row carries no meaning).
              selected={narrowed && (selectedSet?.has(p.id) ?? false)}
              active={open?.id === p.id}
              onClick={() => setOpen((o) => (o?.id === p.id && o.pinned ? null : { id: p.id, pinned: true }))}
            />
            {open?.id === p.id && (
              <div data-provider-hovercard className={cn(BADGE_WRAP, flip ? 'right-0' : 'left-0')} onMouseEnter={cancelClose} onMouseLeave={hideSoon}>
                {card(p)}
              </div>
            )}
          </div>
        ))}
        {rest.length > 0 && (
          <button
            type="button"
            aria-expanded={false}
            aria-label={`Show ${rest.length} more ${rest.length === 1 ? 'provider' : 'providers'}`}
            onClick={() => toggleExpanded(true)}
            className="flex w-10 items-center justify-center self-stretch rounded-lg border border-border-default bg-surface-card text-[length:var(--type-body-size)] font-semibold text-text-secondary hover:border-border-strong hover:text-text-primary"
          >
            +{rest.length}
          </button>
        )}
        {expanded && !wrap && (
          <button
            type="button"
            aria-expanded
            onClick={() => toggleExpanded(false)}
            className="flex w-10 items-center justify-center self-stretch rounded-lg border border-border-default bg-surface-card text-[length:var(--type-body-size)] font-semibold text-text-secondary hover:border-border-strong hover:text-text-primary"
          >
            &minus;
          </button>
        )}
      </div>
      </div>
      </div>
    </div>
  );
}

/** Widths of the loading pills, close to real name chips. */
const SKELETON_CHIPS = [148, 172, 160, 184, 140] as const;
const CHIP_GAP = 8;
const MORE_WIDTH = 48;
/** The badge's width (352). */
const CARD_SPAN = 360;
const CHIP = 'flex items-center gap-3 rounded-lg border border-border-default bg-surface-card text-[length:var(--type-label-size)] font-medium leading-[var(--type-label-line)] whitespace-nowrap hover:border-border-strong py-1';
/** One badge floats on its own: the badge draws its card (16 px corners, band to the edge); the wrapper only lifts it. */
const BADGE_WRAP = 'absolute top-[calc(100%+4px)] z-40 rounded-2xl shadow-[0_12px_32px_rgba(21,27,32,0.16),0_2px_6px_rgba(21,27,32,0.08)]';

/**
 * One provider, minimal: photo, or the fallback letter on a 24 px disc (Antonin, 13 Oct: "if no photo, still show the
 * fallback letter profile picture"; one initial at 12 px, so it stays on the type floor), availability as a dot on it,
 * the name, today's free slots, a dot for a licence issue, and a blue ring when the provider is ticked in the filter.
 */
/** The first letter of the name, past a title ("Dr. Nader" → N). */
const initialOf = (name: string) => (name.replace(/^(dr|prof|mr|mrs|ms)\.?\s+/i, '').trim()[0] ?? '?').toUpperCase();

function MiniChip({ p, selected, active, onClick }: { p: ProviderSummary; selected: boolean; active?: boolean; onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={active}
      aria-label={`${p.name}, ${chipSlotsLabel(p)}${selected ? ', selected' : ''}${p.issue?.message ? `, ${p.issue.message}` : ''}`}
      className={cn(
        CHIP,
        'pl-2 pr-3',
        selected && 'border-brand-primary bg-blue-100',
        active && 'border-brand-primary',
      )}
    >
      <span className="relative flex-shrink-0">
        {p.photo
          ? <PatientAvatar name={p.name} photoUrl={p.photo} variant="provider" size={32} />
          : <span aria-hidden data-avatar-fallback className="grid size-8 place-items-center rounded-lg bg-grey-200 text-[length:var(--type-body-size)] font-medium leading-none text-text-secondary">{initialOf(p.name)}</span>}
        {p.slotsState ? null : <span aria-hidden className={cn('absolute -bottom-1 -right-1 h-2 w-2 rounded-full border-2 border-surface-card', p.available ? 'bg-status-success' : 'bg-grey-400')} />}
      </span>
      <span className="flex flex-col items-start min-w-0 gap-0 text-left">
        <span className={cn('max-w-[144px] truncate leading-tight', selected ? 'text-brand-primary' : 'text-text-primary')}>{p.name}</span>
        {p.specialty && <span className="max-w-[144px] truncate text-[length:var(--type-meta-size)] font-normal leading-tight text-text-muted">{p.specialty}</span>}
      </span>
      <span className={cn(
        'inline-flex h-5 min-w-5 items-center justify-center rounded-md px-1 font-normal tabular-nums text-[length:var(--type-meta-size)]',
        // Red only for a real credential issue (an expired licence carries a message); the backend sends an empty `issue`
        // object for everyone else, which made every count red (Antonin, 15 Oct). Same test as the label and the hover card.
        p.issue?.message ? 'bg-status-error/10 text-status-error' : 'bg-grey-100 text-text-muted',
      )}>{p.slotsState ? '—' : p.slotsToday}</span>
    </button>
  );
}

/** The chip's spoken availability and count; while the slots feed is loading or failed it claims neither. */
function chipSlotsLabel(p: ProviderSummary): string {
  if (p.slotsState === 'error') return "slots couldn't load";
  if (p.slotsState === 'loading') return 'slots loading';
  return `${p.available ? 'available' : 'unavailable'}, ${p.slotsToday} slots today`;
}
